import React from 'react'
import { Button, Alert } from 'react-bootstrap'
import DocumentTitle from 'react-document-title'
import ReactMixin from 'react-mixin'
import ImmutableRenderMixin from 'react-immutable-render-mixin'
import RecordNav from './recordNav'
import TransactionActions from './transactionActions'
import ExhibitTable from '_/shared/components/exhibitTable'
import ConfirmModal from '_/shared/components/confirmModal'
import PersonalMapping from '_/shared/components/personalMapping'
import AssociatedProperties from './associatedProperties'
import createLinkToImmutable from '_/utils/createLinkToImmutable'
import dateToDateTimeString from '_/utils/dateToDateTimeString'
import { Map, List } from 'immutable'

/**
 * Transaction detail higher order component
 */
const TransactionDetail = (FieldData, { type, calcMap, hasCopy }) =>

@ReactMixin.decorate(ImmutableRenderMixin)
class extends React.Component {
  static propTypes = {
    account: React.PropTypes.object.isRequired,
    property: React.PropTypes.object.isRequired,
    exhibits: React.PropTypes.object.isRequired,
    transaction: React.PropTypes.object.isRequired,
    mTransaction: React.PropTypes.object,
  }

  static contextTypes = {
    store: React.PropTypes.object,
    notify: React.PropTypes.func,
    router: React.PropTypes.func
  }

  static fetchData (store, { params, query = {} }) {
    const isNew = params.transId === 'new'
    return {
      property: store.get(`/properties/${params.id}`, { expanded: true }),
      exhibits: !isNew && store.get(`/${type}s/${params.transId}/exhibits`),
      transaction: !isNew && store.get(`/${type}s/${params.transId}`, { expanded: true }),
      mTransaction: isNew && query.transaction && store.get(`/${type}s/${query.transaction}`)
    }
  }

  constructor (props, context) {
    super(props, context)
    this.setStateFromProps(props, context)
  }
  componentWillReceiveProps (...args) { this.setStateFromProps(...args) }

  setStateFromProps (props, context) {
    let { mTransaction } = props
    const { property, exhibits, transaction } = props
    const isNew = !transaction
    const properties = isNew ? List([ property ]) : transaction.get('properties')
    const isEdit = !!context.router.getCurrentQuery().isEdit
    const canEdit = props.transaction && !props.account.get('isAdmin') === props.transaction.get('isPrivate')

    if (mTransaction) {
      mTransaction = mTransaction.delete('id')
    }

    this.state = {
      isNew, properties, property,
      transaction: mTransaction || transaction || Map({ properties: List([ property ]) }),
      undoTransaction: mTransaction || transaction || Map({ properties: List([ property ]) }),
      exhibits: exhibits && exhibits.get('data'),
      editing: isNew || (isEdit && canEdit),
      link: createLinkToImmutable(this, 'transaction', calcMap)
    }
  }

  handleExhibitChange (exhibits) {
    this.setState({ exhibits })
  }

  handlePropertyChange (properties) {
    this.setState({ properties })
  }

  async handleEditPublic () {
    const { store, router } = this.context
    const { property, transaction } = this.props
    const id = property.get('id')
    const transId = transaction.get('id')

    try {
      const prvProp = await store.post(`/records/${id}/sync`)
      const updatedTrans = await store.get(`/${type}s/${transId}`, { expanded: true })
      router.transitionTo(type, {
        id: prvProp.get('id'),
        transId: updatedTrans.getIn(['private','id'])
      }, { isEdit: true })
    } catch (er) {
      console.error(er)
    }
  }

  handleEditToggle () {
    const { account } = this.props
    const { notify } = this.context
    const { transaction, editing, isNew, undoTransaction } = this.state
    if (isNew) return

    const isPublicEditForNonAdmin = !account.get('isAdmin') && !transaction.get('isPrivate')
    if (isPublicEditForNonAdmin) return this.handleEditPublic()

    if (editing && !transaction.equals(undoTransaction)) {
      return notify('Please save or reject changes')
    }
    this.setState({ editing: !editing, undoTransaction: transaction })
  }

  handleShareToggle () {
    const { property } = this.state
    this.setState({ property: property.set('unsharable', !property.get('unsharable')) })
  }

  undoChanges () {
    this.setState({
      editing: false,
      transaction: this.state.undoTransaction
    })
  }

  async share () {
    const { transaction } = this.state
    const { store, notify } = this.context
    const id = transaction.get('id')

    try {
      const sharedTrans = await store.post(`/${type}s/${id}/share?expanded=true`)
      this.setState({ transaction: sharedTrans })
      notify('Transaction data shared')
    } catch (er) {
      console.error(er)
      notify(er.response.body.error)
    }
  }

  async unlock () {
    const { transaction } = this.state
    const { store, notify } = this.context
    const id = transaction.get('id')

    try {
      const unlockedTrans = await store.post(`/${type}s/${id}/unlock?expanded=true`)
      this.setState({ transaction: unlockedTrans })
      notify('Transaction data unlocked')
    } catch (er) {
      console.error(er)
      notify(er.response.body.error)
    }
  }

  async certifyField (field) {
    const { account } = this.props
    const { transaction } = this.state
    const { store, notify } = this.context
    const id = transaction.get('id')

    try {
      await store.post(`/${type}s/${id}/certifyField`, { field })
      this.setState({
        transaction: transaction.setIn(['fieldData', field, 'certById'], account.get('id'))
      })
      notify('Field certified successfully')
    } catch (er) {
      console.error(er)
      notify(er.response.body.error)
    }
  }

  async updateTransaction (evt) {
    const { transaction, properties, property } = this.state
    const { store, notify, router } = this.context
    const id = transaction.get('id')
    const data = transaction.set('properties', properties.map(p => p.get('id')))

    try {
      const updatedTransaction = await store.post((id ? `/${type}s/${id}` : `/${type}s`)+'?expanded=true', data)
      this.setState({ editing: false, transaction: transaction.mergeDeep(updatedTransaction) })
      if (id) notify('Data updated')
      router.transitionTo(type, {
        id: property.get('id'),
        transId: updatedTransaction.get('id')
      })
    } catch (er) {
      this.setState({
        errors: er.response.body.details
      })
    }
    global.scrollTo(0, 0)
  }

  openDestroyModal () {
    this.setState({ showDestroyModal: true })
  }

  cancelDestroyModal () {
    this.setState({ showDestroyModal: false })
  }

  async destroyTransaction (evt) {
    const { transaction, property } = this.props
    const { store, router, notify } = this.context
    const id = transaction.get('id')

    try {
      await store.delete(`/${type}s/${id}`)
      router.transitionTo('record', { id: property.get('id') })
    }
    catch (er) {
      console.error(er)
      notify(er.response.body.error)
    }
  }

  render() {
    const { account } = this.props
    const { metadata } = this.context.store
    const { isNew, editing, property, exhibits, properties, transaction, undoTransaction, link, errors, showDestroyModal } = this.state
    const isPrivate = transaction.get('isPrivate')

    return (
      <DocumentTitle title={property.get('address')}>
        <div>
          <RecordNav property={property} account={account} onShareToggle={::this.handleShareToggle}>
            <TransactionActions
              type={type}
              account={account}
              property={property}
              transaction={undoTransaction}
              editing={editing}
              isNew={isNew}
              hasCopy={hasCopy}
              handleEdit={::this.handleEditToggle} />
          </RecordNav>

          {isPrivate && transaction.has('pointsAvailable') && !property.get('unsharable') &&
            <Alert>
              <Button
                onClick={::this.share}
                bsStyle="info"
                bsSize="xsmall"
                className="pull-right">
                Share
              </Button>
              Earn <b>{transaction.get('pointsAvailable')} points</b>&nbsp;
              (or more when your data is certified by others) by sharing this transaction publicly.
            </Alert>
          }

          {!isPrivate && transaction.has('pointsWorth') &&
            <Alert bsStyle="success">
              <Button
                bsStyle="success"
                bsSize="xsmall"
                onClick={::this.unlock}
                className="pull-right">
                Unlock
              </Button>
              For <b>{transaction.get('pointsWorth')} points</b> you can unlock this transaction,&nbsp;
              including exhibits, and enjoy unlimited future updates for free.
            </Alert>
          }

          {!isNew && !editing &&
            <p style={{marginTop: 15}}><i>Effective date {dateToDateTimeString(transaction.get('lastMod'))}</i></p>
          }

          <AssociatedProperties
            currentProperty={property}
            properties={properties}
            onChange={::this.handlePropertyChange}
            editable={editing} />

          <FieldData
            account={account}
            transaction={transaction}
            onCertify={::this.certifyField}
            metadata={metadata.get(type)}
            errors={errors}
            editing={editing}
            link={link} />

          {isPrivate &&
            <PersonalMapping
              account={account}
              type={type}
              link={link}
              editable={editing}
              data={transaction.get('customFlds')}
              mapping={transaction.get('personalMapping')} />
          }

          {!isNew &&
            <ExhibitTable
              account={account}
              exhibits={exhibits}
              exhibitMetadata={metadata.get('exhibit')}
              transaction={undoTransaction}
              onChange={::this.handleExhibitChange}
              editable={editing} />
          }

          {editing &&
            <Button
              className="hotkeys-save"
              onClick={::this.updateTransaction}
              bsStyle="primary">
              {isNew ? 'Create new' : 'Save changes'}
            </Button>
          }
          {editing && !isNew &&
            <span>
              <Button
                className="pull-right"
                onClick={::this.openDestroyModal}
                bsStyle="danger">
                Remove record
              </Button>
              &nbsp;
              <Button
                className="hotkeys-undo"
                onClick={::this.undoChanges}
                bsStyle="warning">
                Reject changes
              </Button>
            </span>
          }
          {showDestroyModal &&
            <ConfirmModal
              actionText="Remove this transaction"
              onAction={::this.destroyTransaction}
              onCancel={::this.cancelDestroyModal}
              bsStyle="danger"
              message={
                <p>
                  This will remove this transaction from <b className="text-danger">ANY</b> associated properties.&nbsp;
                  If you wish to move this transaction to another property, edit its associated properties instead.
                </p>
              }
            />
          }
        </div>
      </DocumentTitle>
    )
  }
}

export default TransactionDetail



// WEBPACK FOOTER //
// ./modules/shared/pages/record/transactionDetail.js